/**
 * VirtualList —— virtualizer 纯逻辑核心的 DOM 接线（滚动语义见 ./virtualizer.ts 文件头）。
 *
 * 接线口径：
 * - 所有突变（items 变化 / RO 测高 / 视口变化 / 用户滚动 / API 调用）都进核心后
 *   bump() 重渲染，渲染只读 snapshot()——组件本身不保存任何滚动状态副本。
 * - pendingScrollTop 只在「DOM 已与最新快照一致」的 commit 写回（对比 totalHeight），
 *   否则跳过等下个 commit——对着旧 DOM 写 scrollTop 会被浏览器夹断，anchor 就偏了。
 * - 滚动回声不做区分：程序写 scrollTop 触发的 scroll 事件照常喂 setScrollTop，
 *   核心幂等（7a 测试锁定）。
 * - 测高：一个 ResizeObserver 同时管容器（视口高变化=软键盘/窗口缩放）和所有行；
 *   行挂载即 observe，RO 的初始回调就是首次测量，无单独测量通道。RO 回调在本帧
 *   layout 后、paint 前执行，Preact 微任务重渲染也在 paint 前完成——首帧不闪估高布局。
 * - 触顶加载：每次 commit 后查 gate（listDom.ts）——prepend 补偿后没有滚动事件、
 *   短历史填不满屏这两种情况也能继续加载；items 不变则熄火防连环触发。
 * - content-visibility: auto 评估结论：不用。我们已自研虚拟化（二者重复记账），
 *   且它依赖 contain-intrinsic-size、Safari 18 才支持（支持下限 iOS 15）。
 *   浏览器原生滚动锚定（overflow-anchor）必须关——补偿由核心全权负责，双重补偿会跳。
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX, RefObject } from 'preact';
import { createVirtualizer, type VirtualAnchor } from './virtualizer';
import { createReachTopGate, entryHeight } from './listDom';
import styles from './virtualList.module.scss';

export interface VirtualListApi {
  scrollToBottom(): void;
  /** 跳转到指定 key 的行（已完整可见则不滚动）。false = key 不在当前数据中。 */
  scrollToKey(key: string): boolean;
  /** 当前滚动 anchor（可序列化，任务 13 冷启动恢复滚动位置用）。 */
  getAnchor(): VirtualAnchor;
  /** 恢复持久化的 anchor；key 已不存在则回底部。 */
  restoreAnchor(anchor: VirtualAnchor): void;
}

export interface VirtualListProps<T> {
  /** 时间线升序的完整数据；虚拟化由本组件负责，调用方不截断。 */
  items: readonly T[];
  /** 行 key：稳定且唯一（消息用 clientId，任务 10 口径）。函数引用须稳定。 */
  itemKey: (item: T) => string;
  renderItem: (item: T) => ComponentChildren;
  /** 未测量行的估计高度（px）。仅取挂载时值。 */
  estimateHeight: number;
  /**
   * 滚动位置距列表顶 ≤ reachTopPx 时触发（items 每变化一次至多一次）。
   * 没有更多历史时调用方自行忽略即可，不变更 items 就不会再触发。
   */
  onReachTop?: () => void;
  /** 默认 300。仅取挂载时值。 */
  reachTopPx?: number;
  /** 粘底状态变化（false = 用户在看历史，「滚到底」按钮据此显示）。挂载时回调一次初始值。 */
  onFollowChange?: (follow: boolean) => void;
  apiRef?: RefObject<VirtualListApi | null>;
  className?: string;
}

const DEFAULT_REACH_TOP_PX = 300;

export function VirtualList<T>(props: VirtualListProps<T>) {
  const { items, itemKey, renderItem, apiRef, onReachTop, onFollowChange } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const roRef = useRef<ResizeObserver | null>(null);
  const [, setTick] = useState(0);
  const bump = useCallback(() => setTick((n) => n + 1), []);
  // useState 惰性初始化（刻意不用 useMemo）：estimateHeight/reachTopPx 只取挂载时值
  const [virt] = useState(() => createVirtualizer({ estimateHeight: props.estimateHeight }));
  const [gate] = useState(() => createReachTopGate(props.reachTopPx ?? DEFAULT_REACH_TOP_PX));
  const [keyByEl] = useState(() => new WeakMap<Element, string>());

  const keys = useMemo(() => items.map((it) => itemKey(it)), [items, itemKey]);
  const itemByKey = useMemo(() => {
    const m = new Map<string, T>();
    for (const it of items) m.set(itemKey(it), it);
    return m;
  }, [items, itemKey]);

  const snap = virt.snapshot();

  // 视口测量 + ResizeObserver（容器与行共用一个；首个带行的 commit 前必然已创建——
  // 首次 commit 时核心还没有 items，快照恒空）
  useLayoutEffect(() => {
    const el = containerRef.current!;
    const ro = new ResizeObserver((entries) => {
      const measured: Array<readonly [string, number]> = [];
      for (const entry of entries) {
        if (entry.target === el) continue;
        const key = keyByEl.get(entry.target);
        if (key !== undefined) measured.push([key, entryHeight(entry)] as const);
      }
      virt.setViewportHeight(el.clientHeight);
      if (measured.length > 0) virt.measureMany(measured);
      bump();
    });
    roRef.current = ro;
    ro.observe(el);
    virt.setViewportHeight(el.clientHeight);
    bump();
    return () => {
      roRef.current = null;
      ro.disconnect();
    };
  }, [virt, keyByEl, bump]);

  // items → 核心（prepend/删除的锚点补偿在核心内完成）
  useLayoutEffect(() => {
    virt.setItems(keys);
    gate.onItems(keys[0], keys.length);
    bump();
  }, [keys, virt, gate, bump]);

  useLayoutEffect(() => {
    if (!apiRef) return;
    apiRef.current = {
      scrollToBottom() {
        virt.scrollToBottom();
        bump();
      },
      scrollToKey(key) {
        const found = virt.scrollToKey(key);
        if (found) bump();
        return found;
      },
      getAnchor: () => virt.getAnchor(),
      restoreAnchor(anchor) {
        virt.restoreAnchor(anchor);
        bump();
      },
    };
    return () => {
      apiRef.current = null;
    };
  }, [apiRef, virt, bump]);

  const follow = snap.follow;
  useLayoutEffect(() => {
    onFollowChange?.(follow);
  }, [follow, onFollowChange]);

  // 每次 commit 后：DOM 与最新快照一致才写回 pendingScrollTop + 查触顶门控；
  // 不一致说明突变刚进核心、重渲染在路上（已 bump），下个 commit 再写
  const renderedTotal = snap.totalHeight;
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const latest = virt.snapshot();
    if (latest.totalHeight !== renderedTotal) return;
    const pending = virt.takePendingScrollTop();
    if (pending !== null) el.scrollTop = pending;
    if (gate.check(latest.scrollTop, el.clientHeight)) onReachTop?.();
  });

  const register = useCallback(
    (key: string, el: HTMLElement) => {
      keyByEl.set(el, key);
      roRef.current?.observe(el);
      return () => {
        roRef.current?.unobserve(el);
        keyByEl.delete(el);
      };
    },
    [keyByEl],
  );

  const onScroll = (e: JSX.TargetedEvent<HTMLDivElement>) => {
    // 先同步视口高再喂 scrollTop：容器变矮/变高（输入栏引用条收展、autosize、
    // 键盘 inset）时浏览器会钳 scrollTop 并先于 RO 回调派发 scroll 事件——用旧
    // viewport 算 maxScroll 会把「仍在底部」误判成用户上滚，粘底 follow 丢失
    // （14.6 轮实证）。viewport 未变时 setViewportHeight 幂等零开销。
    virt.setViewportHeight(e.currentTarget.clientHeight);
    virt.setScrollTop(e.currentTarget.scrollTop);
    bump();
  };

  const cls = props.className !== undefined ? `${styles.viewport} ${props.className}` : styles.viewport;
  return (
    <div ref={containerRef} className={cls} onScroll={onScroll} data-testid="virtual-list">
      <div className={styles.spacer} style={{ height: `${snap.totalHeight}px` }}>
        {snap.items.map((pos) => {
          const item = itemByKey.get(pos.key);
          // 快照可能比 items 旧一拍（items effect 本 commit 才跑）：缺失的行跳过，下个 commit 修正
          if (item === undefined) return null;
          return (
            <Row key={pos.key} itemKey={pos.key} top={pos.top} register={register}>
              {renderItem(item)}
            </Row>
          );
        })}
      </div>
    </div>
  );
}

interface RowProps {
  itemKey: string;
  top: number;
  /** 挂载时登记进 RO（初始回调即首次测量），返回解除函数。 */
  register: (key: string, el: HTMLElement) => () => void;
  children: ComponentChildren;
}

function Row({ itemKey, top, register, children }: RowProps) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => register(itemKey, ref.current!), [itemKey, register]);
  return (
    <div ref={ref} className={styles.row} style={{ top: `${top}px` }}>
      {children}
    </div>
  );
}
